import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, UserPlus, KeyRound, Copy, Check, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function UsersDialog({ open, onOpenChange, subs }) {
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("owner");
  const [subId, setSubId] = useState("");
  const [busy, setBusy] = useState(false);
  const [resetInfo, setResetInfo] = useState(null); // { id, email, password, ghl_delivered }
  const [resetting, setResetting] = useState(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const subName = (id) => subs.find((s) => s.id === id)?.name || id;

  const create = async () => {
    setBusy(true);
    try {
      const payload = { email, password, role };
      if (role === "owner") payload.subaccount_id = subId;
      await api.post("/users", payload);
      toast.success(role === "admin" ? "Admin created" : "Owner login created");
      setEmail("");
      setPassword("");
      setSubId("");
      setRole("owner");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Login removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const resetPassword = async (u) => {
    setResetting(u.id);
    setResetInfo(null);
    setCopied(false);
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setResetInfo({ id: u.id, email: data.email, password: data.temporary_password, ghl_delivered: data.ghl_delivered });
      toast.success(data.ghl_delivered ? "Password reset & sent via GHL" : "Password reset");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setResetting(null);
    }
  };

  const copyPw = async () => {
    try {
      await navigator.clipboard.writeText(resetInfo.password);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* ignore */
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Team & Owner Logins</DialogTitle>
          <DialogDescription>Create owner logins (scoped to one sub-account) or admin logins (full access). Admins can create other admins.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-lg border border-border p-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Role</Label>
            <Select value={role} onValueChange={(v) => { setRole(v); if (v === "admin") setSubId(""); }}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-popover">
                <SelectItem value="owner">Owner (one sub-account)</SelectItem>
                <SelectItem value="admin">Admin (full access)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Email</Label>
              <Input data-testid="owner-email-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="user@store.com" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Password</Label>
              <Input data-testid="owner-password-input" type="text" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="temp password" />
            </div>
          </div>
          {role === "owner" && (
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-wider text-muted-foreground">Assign Sub-Account</Label>
              <Select value={subId} onValueChange={setSubId}>
                <SelectTrigger data-testid="owner-subaccount-select"><SelectValue placeholder="Select a sub-account" /></SelectTrigger>
                <SelectContent className="bg-popover">
                  {subs.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name} · {s.ghl_subaccount_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button data-testid="owner-create-btn" onClick={create} disabled={busy || !email || !password || (role === "owner" && !subId)} className="w-full gap-2">
            {role === "admin" ? <ShieldCheck className="h-4 w-4" /> : <UserPlus className="h-4 w-4" />}
            {busy ? "Creating…" : role === "admin" ? "Create Admin" : "Create Owner Login"}
          </Button>
        </div>

        <div className="space-y-2" data-testid="owners-list">
          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No logins yet.</p>
          ) : (
            users.map((u) => (
              <div key={u.id} className="rounded-md border border-border/70 p-3" data-testid={`owner-row-${u.id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {u.email}
                      {u.role === "admin" && (
                        <span data-testid={`user-admin-badge-${u.id}`} className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-primary">
                          <ShieldCheck className="h-3 w-3" /> Admin
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] font-mono text-muted-foreground">{u.role === "admin" ? "Full access" : subName(u.subaccount_id)}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" data-testid={`owner-reset-${u.id}`} disabled={resetting === u.id} onClick={() => resetPassword(u)} className="h-8 gap-1.5 text-[11px]">
                      <KeyRound className="h-3.5 w-3.5" /> {resetting === u.id ? "Resetting…" : "Reset password"}
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`owner-delete-${u.id}`} onClick={() => remove(u.id)} className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                {resetInfo?.id === u.id && (
                  <div className="mt-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 space-y-1.5" data-testid={`owner-reset-result-${u.id}`}>
                    <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Temporary password</div>
                    <div className="flex items-center gap-2">
                      <code data-testid={`owner-temp-password-${u.id}`} className="flex-1 rounded bg-black/50 px-2 py-1 font-mono text-sm text-emerald-400 break-all">{resetInfo.password}</code>
                      <Button variant="outline" size="icon" data-testid={`owner-copy-password-${u.id}`} onClick={copyPw} className="h-8 w-8 shrink-0">
                        {copied ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      {resetInfo.ghl_delivered
                        ? "Also written to the owner's GHL contact field — your workflow will email it to them."
                        : "Share this with the owner securely. (Not sent via GHL — no matching contact or GHL not configured.)"}
                    </p>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
